import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axiosInstance, {
  checkTokenValidity,
  CheckUserExpiry,
  getUserDetails,
  canEditSamplingFishData,
  handleLegacySessionExpired,
} from "../../components/content/axiosConfig";
import { resolveScopedFisherID } from "../../utils/pondListForUser";
import Preloader from "../../components/custom/preLoader";
import showErrorToast from "../../components/toast/toast";
import PondDetailWorkflowPageHeader from "../../components/custom/PondDetailWorkflowPageHeader";
import PondDetailWorkflowTabs from "../../components/custom/PondDetailWorkflowTabs";
import SamplingDetailGallery from "../../components/sampling/SamplingDetailGallery";

/**
 * Session-level photo gallery for a sampling detail (not per fish sample).
 */
const SamplingGalleryViaPondDetail = () => {
  const { id } = useParams();
  const userDetails = getUserDetails();
  const canEdit = canEditSamplingFishData(userDetails);
  const [pond, setPond] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadDetail = useCallback(async () => {
    try {
      setIsLoading(true);
      const details = getUserDetails();
      const detailParams = { id };
      const scoped = resolveScopedFisherID(details);
      if (scoped != null && scoped !== "") {
        detailParams.FisherID = scoped;
      }
      const res = await axiosInstance.get(`/api/PondDetail/GetPondDetail`, {
        params: detailParams,
      });
      setPond(res.data);
    } catch (error) {
      if (error.response?.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      } else {
        showErrorToast(error.response?.data?.message || "Failed to load sampling session");
      }
      setPond(null);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    checkTokenValidity();
    CheckUserExpiry();
    loadDetail();
  }, [loadDetail]);

  if (isLoading) return <Preloader />;

  return (
    <div className="donezo-page donezo-list-page donezo-orders-page donezo-workflow-page donezo-upload-page">
      <div className="container-fluid px-3 px-md-4 py-3 py-md-4">
        <PondDetailWorkflowPageHeader
          pondDetailId={id}
          section="gallery"
          detailName={pond?.Name}
          noBottomMargin
        />

        <div className="mt-3">
          <PondDetailWorkflowTabs id={id} active="gallery" />
        </div>

        <div className="donezo-orders-panel donezo-upload-section">
          <div className="donezo-upload-section__header">
            <i className="fa fa-images" aria-hidden />
            <h2 className="donezo-upload-section__title">Sampling gallery</h2>
          </div>
          <div className="donezo-upload-section__body">
            <p className="donezo-upload-section__hint">
              Photos for this sampling session. These are not linked to individual fish samples.
            </p>
            <SamplingDetailGallery pondDetailId={id} canEdit={canEdit} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SamplingGalleryViaPondDetail;
